import React, { useCallback, useEffect, useState } from "react";
import useCommonStore from "../../../../stores/common";
import useOrganizationStore from "../../../../stores/organization";
import useUserStore from "../../../../stores/user.js";
import style from "./styles.module.scss";
import toast from "react-hot-toast";
import Button from "../../../../components/new-button";
import TextInput from "../../../../components/form/text-input";
import { MentionsInput, Mention } from "react-mentions";
import ConfirmationModal from "../../../application/components/ConfirmationModal/ConfirmationModal";
import NoteItem from "./NoteItem";
import SendIcon from "../../../../assets/svg/icons/common/SendIcon";
import {
  getOrganizationNotes,
  addOrganizationNotes,
  deleteOrganizationNotes,
} from "../../../../../api";



const Notes = () => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const loggedInUser = useUserStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [noteContent, setNoteContent] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.id,
  );

  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData
  );

  const fetchData = useCallback(async () => {
    if (!selectedOrganizationId) return;

    setLoading(true);
    try {
      const noteResponse = await getOrganizationNotes(selectedOrganizationId);

      // Normalize into an array safely
      let newNotes = noteResponse?.notes ?? noteResponse?.data?.notes ?? noteResponse?.data ?? [];
      if (!Array.isArray(newNotes)) newNotes = Object.values(newNotes);

      setNotes(Array.isArray(newNotes) ? [...newNotes] : []);
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Error loading notes", { duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [selectedOrganizationId]);


  useEffect(() => {
    fetchData();
  }, [selectedOrganizationId]);

  const handleNote = (content) => {
    setNoteContent(content);
  };

  const handleDeletetClick = (noteId) => {
    setConfirmId(null);
    deleteOrganizationNotes(
      noteId, selectedOrganizationId
    )
      .then((response) => {
        if(response.success) {
          // Update both state variables
          setNotes(response.notes);
          setSelectedOrganizationData((prev) => ({
            ...prev,
            notes: response.notes,
          }));
          toast.success("Note deleted successfully", {
            duration: 5000,
          });
        }        
        setNoteContent('');
      })
      .catch((err) => {
        toast.error("Something went wrong!", {
          duration: 5000,
        });
       });
  };

  function formatNote(note) {
    return {
      id: note.id,
      signal: "danger",
      who: loggedInUser?.name,
      created_at: note.created_at,
      content: note.content,
    };
  }

  const onSend = async () => {
    if (!noteContent.trim()) {
      toast.error('Please add a note', { duration: 5000 });
      return;
    }

    const toastId = toast.loading("Adding note...", { duration: 10000 });

    try {
      const formData = new FormData();

      // Add note content
      formData.append('content', noteContent);
      formData.append('stratus', true);

      const response = await addOrganizationNotes(selectedOrganizationId, formData);

      if (response.success) {
        
        const allNotes = response.notes;
        
        // Update both state variables
        setNotes(prevNotes => {
          // find the new record (not already in prevNotes by id)
          const newRecords = allNotes.filter(
            note => !prevNotes.some(existing => existing.id === note.id)
          );

          // format and add only new ones
          const formatted = newRecords.map(formatNote);

          return [...prevNotes, ...formatted];
        });
      

        setSelectedOrganizationData((prev) => ({
          ...prev,
          notes: response.notes,
        }));

        // Still reload notes from the server for data consistency
        setNoteContent('');
        toast.success(
          <div className={style.undo_container}>
            <span>{"Note added successfully"}</span>
          </div>,
          { id: toastId, duration: 5000 }
        );
      } else {
        toast.error(response.message, { duration: 5000, id: toastId });
      }
    } catch (err) {
      toast.error("Something went wrong!", {
        duration: 5000,
        id: toastId
      });
    } finally {
    }
  };


  return (
      <div
        className={`${style.note_section} ${expandDetails ? style.expanded : ""}`}
      >
        <div className={style.head}>
          <h6>Notes</h6>
          <div> 
            {/* slidingTabOptions */}
          </div>
        </div>

        <div className={style.notes_container}>
          {/* <p className={style.month_title} >October 2024</p> */}
          {!loading && notes?.map((note, index) => (
            <React.Fragment key={note?.id ?? index}>
              <NoteItem
                note={note}
                onDeletetClick={() => setConfirmId(note.id)}
              />

            </React.Fragment>
          ))}
          
          <ConfirmationModal
            open={!!confirmId}
            onClose={() => setConfirmId(null)}
            message={
              "Are you sure you want to delete this note?"
            }
            onConfirmClick={() => handleDeletetClick(confirmId)}
          />
        </div>

        <div className={style.footer}>
          


          <div className={style.footer_content}>
            <MentionsInput
              value={noteContent}
              onChange={(e, newValue, newPlainTextValue, mentions) =>
                handleNote(newPlainTextValue)
              }
              className={style.note_input}
              name="note"
              placeholder={ "Add a Note"}
            >
              <Mention
                trigger="@"
                data={""}
                appendSpaceOnAdd={true}
                displayTransform={(id, display) => `@${display}`}
              />
            </MentionsInput>

            <div className={style.footer_actions}>
              <Button
                bgColor="#4C70EF"
                onClick={onSend}
                disabled={noteContent !== "" ? false : true}
                variant="primary"
                icon={<SendIcon />}
              >
              </Button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Notes;
